import React, { useEffect, useState, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Mail, Smartphone, Save, Loader2, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api/notifications';

const CHANNEL_META = {
  in_app: { label: 'In-App', icon: Bell, hint: 'Bell icon + Notifications page' },
  email:  { label: 'Email',  icon: Mail, hint: 'Sends to your registered email (requires Resend/SendGrid setup)' },
  sms:    { label: 'SMS',    icon: Smartphone, hint: 'Sends to your registered phone (requires Twilio setup)' },
};

const NotificationPreferences = () => {
  const [categories, setCategories] = useState([]);
  const [channels, setChannels] = useState(['in_app', 'email', 'sms']);
  const [prefs, setPrefs] = useState({});
  const [orig, setOrig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/categories`, { withCredentials: true }),
      axios.get(`${API}/preferences`, { withCredentials: true }),
    ]).then(([c, p]) => {
      setCategories(c.data.categories || []);
      setChannels(c.data.channels || ['in_app', 'email', 'sms']);
      setPrefs(p.data.preferences || {});
      setOrig(JSON.parse(JSON.stringify(p.data.preferences || {})));
    }).catch(() => toast.error('Failed to load preferences'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = (cat, ch) => {
    setPrefs((p) => ({
      ...p,
      [cat]: { ...(p[cat] || {}), [ch]: !p[cat]?.[ch] },
    }));
  };
  const setAllForChannel = (ch, val) => {
    setPrefs((p) => {
      const next = { ...p };
      categories.forEach((c) => {
        next[c.key] = { ...(next[c.key] || {}), [ch]: val };
      });
      return next;
    });
  };

  const isDirty = useMemo(() => JSON.stringify(prefs) !== JSON.stringify(orig), [prefs, orig]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/preferences`, { preferences: prefs }, { withCredentials: true });
      toast.success('Preferences saved');
      setOrig(JSON.parse(JSON.stringify(prefs)));
    } catch (_) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="space-y-5 max-w-4xl" data-testid="notif-prefs-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Bell className="inline-block w-7 h-7 mr-2 mb-1" /> Notification Preferences
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Choose which categories you want to receive — and on which channel.
              <Link to="/notifications" className="ml-2 text-[#0052FF] hover:underline">← Back to inbox</Link>
            </p>
          </div>
          <Button disabled={!isDirty || saving} onClick={save} className="bg-[#0052FF] text-white hover:bg-[#0040CC]" data-testid="save-prefs-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Preferences
          </Button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2 text-sm text-blue-900">
          <Info className="w-4 h-4 mt-0.5" />
          <p>
            <strong>In-App</strong> is always available. <strong>Email</strong> and <strong>SMS</strong> require your tenant admin to configure
            external providers (Resend / SendGrid for email, Twilio for SMS) — until then, those channels are queued but not sent.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="prefs-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                {channels.map((ch) => {
                  const meta = CHANNEL_META[ch] || { label: ch, icon: Bell };
                  const Icon = meta.icon;
                  const allOn = categories.length > 0 && categories.every((c) => prefs[c.key]?.[ch]);
                  return (
                    <th key={ch} className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                          <Icon className="w-3.5 h-3.5" /> {meta.label}
                        </div>
                        <button
                          onClick={() => setAllForChannel(ch, !allOn)}
                          data-testid={`bulk-${ch}`}
                          className="text-[10px] text-[#0052FF] hover:underline normal-case font-medium"
                        >
                          {allOn ? 'turn all off' : 'turn all on'}
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.key} className="border-b border-slate-100 hover:bg-slate-50/40">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-900">{c.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{c.description}</p>
                  </td>
                  {channels.map((ch) => (
                    <td key={ch} className="text-center px-4 py-3">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!prefs[c.key]?.[ch]}
                          onChange={() => toggle(c.key, ch)}
                          data-testid={`pref-${c.key}-${ch}`}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {isDirty && (
          <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-900">
            Unsaved changes — click <strong>Save Preferences</strong> to apply.
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default NotificationPreferences;
